import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useTranslation } from '@/app/components/translations/Translation';
import styled from 'styled-components';
import { darkLava } from '@/app/components/search/styles/colors.ts';
import { useEffect } from 'react';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import type { LOYALTY_CONTEXT, LOYALTY_POSITION } from '@/app/common/starTrack/events/utils/eventsUtils.ts';
import { createLoyaltyBannerShownPayload } from '@/app/common/starTrack/events/loyaltyBannerShown.ts';

type BannerLoyaltyEnrolledProps = {
  discountPercentage: number;
  loyaltyContext: LOYALTY_CONTEXT;
  loyaltyPosition: LOYALTY_POSITION;
  isLoggedIn: boolean;
} & React.HTMLAttributes<HTMLDivElement>;
export const BannerLoyaltyEnrolled = ({
  discountPercentage,
  loyaltyContext,
  loyaltyPosition,
  isLoggedIn,
  ...rest
}: BannerLoyaltyEnrolledProps) => {
  const { starTrack } = useCommonClient();
  const { commonData, localization, searchInfoData } = usePageWrapperData();
  const { imageBasePath } = localization;
  const { t } = useTranslation();
  const topSwirl = `${imageBasePath}icons/color/top-swirl-details.svg`;
  const topSwirlSmall = `${imageBasePath}icons/color/top-swirl-details-small.svg`;
  const logo = `${imageBasePath}icons/color/loyalty.svg`;

  useEffect(() => {
    starTrack.loyaltyBannerShown(
      createLoyaltyBannerShownPayload({
        searchId: searchInfoData.searchId,
        context: loyaltyContext,
        position: loyaltyPosition,
        isLoggedIn,
        localization,
        commonData,
      })
    );
  }, [searchInfoData.searchId, loyaltyContext, loyaltyPosition, isLoggedIn]);

  return (
    <Container topSwirl={topSwirl} topSwirlSmall={topSwirlSmall} {...rest}>
      <IconContainer>
        <img
          src={logo}
          alt=""
          css={`
            height: 100%;
          `}
        />
      </IconContainer>
      <TextContainer>
        <Title>{t('vacation.select.loyalty.banner.logged.title', { percent: discountPercentage.toString() })}</Title>
        <SubtitleContainer>
          <img src={`${imageBasePath}icons/color/pink-circle-check.svg`} alt="" />
          <Subtitle>{t('vacation.select.loyalty.banner.logged.subtitle')}</Subtitle>
        </SubtitleContainer>
      </TextContainer>
    </Container>
  );
};

const Container = styled.div<{ topSwirl: string; topSwirlSmall: string }>`
  display: flex;
  flex-direction: row;
  column-gap: 24px;
  align-items: center;
  padding: 10px 16px;
  background-color: ${darkLava};
  color: ${(p) => p.theme.kallisto.color.text.static.textOnDark};

  background-image: url(${(p) => p.topSwirlSmall});
  background-repeat: no-repeat;
  background-position: right top;
  background-size: 36%;

  @media ${customCss.breakpoints.sm} {
    border-radius: 8px;
    column-gap: 80px;
    padding: 17px 32px;
    background-image: url(${(p) => p.topSwirl});
    background-size: 50%;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  width: auto;
  height: 20px;

  @media ${customCss.breakpoints.sm} {
    height: 32px;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media ${customCss.breakpoints.sm} {
    gap: 6px;
  }
`;

const Title = styled.span`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;

  @media ${customCss.breakpoints.sm} {
    font-size: 24px;
    line-height: 20px;
  }
`;

const SubtitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  img {
    width: 12px;
    @media ${customCss.breakpoints.sm} {
      width: 16px;
    }
  }
`;

const Subtitle = styled.span`
  font-size: 11px;
  line-height: 16px;

  @media ${customCss.breakpoints.sm} {
    font-size: 14px;
    line-height: 20px;
  }
`;
